import { skipToken } from '@reduxjs/toolkit/query';
import { useAppSelector } from '@/app/hooks';
import { selectCurrentUser } from '@mmef/shared/auth';
import {
  useListSavedEventsQuery,
  useSaveEventMutation,
  useUnsaveEventMutation,
} from '@/features/account/savedEventsApi';

/**
 * Bookmark toggle for an event.
 *
 * <p>Says "Save", never "Attending" or "Going". Tickets are sold on the organiser's own site, so
 * this app genuinely doesn't know who turns up — and on a site whose purpose is meeting strangers
 * in person, implying otherwise would be a meaningful lie rather than a loose word.
 *
 * <p>Reads the whole saved list rather than asking about one event: every button on the page then
 * shares a single cache entry, instead of firing a request per card. The account page needs that
 * same list anyway, so it's usually already loaded.
 *
 * <p>Renders nothing for signed-out visitors — a button that only ever bounces you to sign-in is
 * worse than no button.
 */
export function SaveEventButton({ eventId, className = '' }: { eventId: number; className?: string }) {
  const user = useAppSelector(selectCurrentUser);
  const { data: saved } = useListSavedEventsQuery(user ? undefined : skipToken);
  const [saveEvent, saveState] = useSaveEventMutation();
  const [unsaveEvent, unsaveState] = useUnsaveEventMutation();

  if (!user) return null;

  const isSaved = saved?.some((bookmark) => bookmark.eventId === eventId) ?? false;
  const isBusy = saveState.isLoading || unsaveState.isLoading;

  return (
    <button
      type="button"
      className={`button button--save${isSaved ? ' button--save-on' : ''} ${className}`.trim()}
      aria-pressed={isSaved}
      disabled={isBusy}
      onClick={() => (isSaved ? unsaveEvent(eventId) : saveEvent(eventId))}
    >
      <span aria-hidden="true">{isSaved ? '★' : '☆'}</span> {isSaved ? 'Saved' : 'Save'}
    </button>
  );
}
